// Where a call's voices come out (docs/PARITY.md P145) — the apps' audio route picker (Auricular /
// Altavoz / Bluetooth / Auriculares, `Strings.Call.route*`) on a browser: `HTMLMediaElement.setSinkId`
// over the `audiooutput` devices. A page cannot see an "earpiece"; it sees named outputs, so each is
// listed by its own label and tagged with the apps' route word when the label says which it is.
// The choice is remembered per browser (like the camera, CallCameraChoice).

export type AudioRouteKind = 'speaker' | 'bluetooth' | 'wired';

export interface CallAudioOutput {
  id: string;
  name: string;
  kind: AudioRouteKind;
}

export const CallAudioOutputChoice = {
  storageKey: 'satori.call.audioOutput',
  /** The browser's own default route (what `setSinkId('')` means). */
  defaultId: 'default',

  /** The apps' route word for a device label (headset names first: "Bluetooth headset" is Bluetooth). */
  kindOf(label: string): AudioRouteKind {
    const l = label.toLowerCase();
    if (/bluetooth|airpods|\bbt\b|hands-free|buds/.test(l)) return 'bluetooth';
    if (/headphone|headset|auricular|earphone|aud[ií]fono|wired|jack|usb audio/.test(l)) return 'wired';
    return 'speaker';
  },

  /** `enumerateDevices()` → the outputs, the browser's "default"/"communications" aliases folded away
   *  (they duplicate a real device), each named by its label or "<fallback> N". */
  fromDevices(devices: Array<{ kind: string; deviceId: string; label: string }>, fallbackName: string): CallAudioOutput[] {
    const out: CallAudioOutput[] = [];
    let n = 0;
    for (const d of devices) {
      if (d.kind !== 'audiooutput' || !d.deviceId || d.deviceId === 'default' || d.deviceId === 'communications') continue;
      n++;
      out.push({ id: d.deviceId, name: d.label || `${fallbackName} ${n}`, kind: CallAudioOutputChoice.kindOf(d.label) });
    }
    return out;
  },

  /** The output to use: the remembered one while it is still plugged in, else the browser default. */
  resolve(outputs: CallAudioOutput[], saved: string | null): string {
    return saved && outputs.some((o) => o.id === saved) ? saved : CallAudioOutputChoice.defaultId;
  },

  /** A picker is worth offering only when the browser can route AND there is more than one place to route to. */
  offered(canRoute: boolean, outputs: CallAudioOutput[]): boolean {
    return canRoute && outputs.length > 1;
  },
};
